import { getCurrentLanguage, Locale } from '@porsche-code/kado-i18n';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  ContentWrapper,
  LanguageSelect,
  Footer as StyledFooter,
} from '@porsche-code/kado-ui';
import { PLinkPure, PText } from '@porsche-design-system/components-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { PersonActionKind, usePersonContext } from '@/context';
import {
  MeQuery,
  useMeQuery,
  useUpdatePersonSettingsMutation,
} from '@/graphql';

import { version } from '../../package.json';

const Content = styled('div', {
  base: {
    display: 'grid',
    gap: 'md',
    alignItems: 'center',
    gridTemplateColumns: '1fr',
    sm: {
      gridTemplateColumns: '1fr minmax(7em, auto)',
    },
    marginBottom: 'lg',
  },
});

const Navigation = styled('ul', {
  base: {
    listStyleType: 'none',
    padding: 0,
  },
});

const NavigationItem = styled('li', {
  base: {
    margin: 0,
    padding: 0,
    paddingInlineEnd: 'lg',
    display: 'inline-block',
  },
});

export const Footer = () => {
  const queryClient = useQueryClient();
  const { dispatch: dispatchPerson, state: personState } = usePersonContext();
  const {
    t,
    i18n: { language, changeLanguage, isInitialized },
  } = useTranslation(NAMESPACES);

  const [currentYear] = useState(() => new Date().getFullYear());

  const availableLocales =
    useQuery<Locale[]>({
      queryKey: ['availableLocales'],
      queryFn: async () => {
        const res = await fetch(`locales/locales.json`);
        return await res.json();
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }).data || [];

  const currentLanguage = getCurrentLanguage(language, availableLocales);

  const { mutate: updatePersonSettings } = useUpdatePersonSettingsMutation({
    onSuccess: (data) => {
      const cacheKey = useMeQuery.getKey({ isAuthenticated: true } as never);
      const cache = queryClient.getQueryData<MeQuery>(cacheKey);

      if (cache?.me && cache.me.__typename === 'Person') {
        queryClient.setQueryData<MeQuery>(cacheKey, {
          me: {
            ...cache.me,
            settings: {
              ...cache.me?.settings,
              ...data.updatePersonSettings.settings,
            },
          },
        });
      }
    },
  });

  const onLanguageUpdate = (locale: string) => {
    changeLanguage(locale);

    if (personState.isAuthenticated && personState.isAuthorized) {
      dispatchPerson({
        type: PersonActionKind.ChangeLocale,
        payload: locale,
      });

      updatePersonSettings({ input: { locale } });
    }
  };

  return (
    <StyledFooter>
      <ContentWrapper>
        <Content>
          <nav>
            <Navigation>
              <NavigationItem>
                <PLinkPure icon="none">
                  <Link to="/imprint">{t('common:footer.imprint')}</Link>
                </PLinkPure>
              </NavigationItem>
              <NavigationItem>
                <PLinkPure icon="none">
                  <Link to="/privacy-policy">
                    {t('common:footer.privacyPolicy')}
                  </Link>
                </PLinkPure>
              </NavigationItem>
              <NavigationItem>
                <PLinkPure icon="none">
                  <Link to="/cookie-policy">
                    {t('common:footer.cookiePolicy')}
                  </Link>
                </PLinkPure>
              </NavigationItem>
              {personState.isAuthenticated && (
                <NavigationItem>
                  <PLinkPure icon="none">
                    <Link to="/help">{t('common:footer.help')}</Link>
                  </PLinkPure>
                </NavigationItem>
              )}
            </Navigation>
          </nav>

          <LanguageSelect
            label={t('common:language.select')}
            disabled={!isInitialized}
            value={currentLanguage}
            locales={availableLocales}
            onChange={onLanguageUpdate}
          />
        </Content>

        <PText color="contrast-medium">
          © {currentYear} Dr. Ing. h.c. F. Porsche AG | Version {version}
        </PText>
      </ContentWrapper>
    </StyledFooter>
  );
};
